import { getAuthHeader } from '#/api/auth';
import { config } from '#/config';
import { Checklist, ResponseMetadata } from '#/types/message';
import logger from "#/utils/logger";

export const fetchImageUrls = async (imageIds: string[]): Promise<Record<string, string>> => {
  try {
    const response = await fetch(
      `${config.backendUrl}/image_urls?image_ids=${imageIds.map(encodeURIComponent).join(',')}`,
      { headers: await getAuthHeader() }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch image URLs');
    }
    const data: { urls: Record<string, string | null> } = await response.json();

    // Filter out nulls and fall back to blob fetch for images without signed URLs
    const result: Record<string, string> = {};
    const fallbackIds: string[] = [];

    for (const id of imageIds) {
      if (data.urls[id]) {
        result[id] = data.urls[id]!;
      } else {
        fallbackIds.push(id);
      }
    }

    // Fallback: fetch blobs from backend for images without source_url
    if (fallbackIds.length > 0) {
      const fallbackUrls = await Promise.all(
        fallbackIds.map(async imageId => {
          try {
            const resp = await fetch(`${config.backendUrl}/image_file?file_name=${imageId}`, {
              headers: await getAuthHeader()
            });
            if (!resp.ok) return null;
            const imageBlob = await resp.blob();
            return URL.createObjectURL(imageBlob);
          } catch {
            return null;
          }
        })
      );
      fallbackIds.forEach((id, i) => {
        if (fallbackUrls[i]) {
          result[id] = fallbackUrls[i]!;
        }
      });
    }

    return result;
  } catch (error) {
    logger.error('Error fetching image URLs:', error);
    return {};
  }
};

export const fetchResponseMetadata = async (sessionId: string): Promise<ResponseMetadata | null> => {
  try {
    const response = await fetch(`${config.backendUrl}/response_metadata?session_id=${sessionId}`, {
      headers: await getAuthHeader()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch related images');
    }
    const data: ResponseMetadata = await response.json();
    return data;
  } catch (error) {
    logger.error('Error fetching related image names:', error);
    return null;
  }
};

export const fetchChecklist = async (checklistId: string): Promise<Checklist> => {
  const response = await fetch(`${config.backendUrl}/checklist?checklist_id=${checklistId}`, {
    headers: await getAuthHeader()
  });
  const data: Checklist = await response.json();
  return data;
};

export const fetchPromptSuggestions = async (sessionId: string): Promise<string[] | null> => {
  try {
    const response = await fetch(`${config.backendUrl}/prompt_suggestions?session_id=${sessionId}`, {
      headers: await getAuthHeader()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch prompt suggestions');
    }
    const data: { suggestions: string[] } = await response.json();
    return data.suggestions;
  } catch (error) {
    logger.error('Error fetching prompt suggestions:', error);
    return null;
  }
};